import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/internal/operators/finalize';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { AdminSettingsService } from 'src/app/admin/servicies/admin-settings.service';
import { FacilityService } from 'src/app/admin/servicies/facility.service';
import { InventoryFacilityMapping, InventoryLocationMappingRequest } from 'src/app/interfaces/inventory-location-mapping-request';
import { InventoryLocationMappingModel } from 'src/app/interfaces/inventory-location-mapping-response-model';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { DropdownOption } from 'src/app/shared/models/common/custom-filiters';
import { InvetoryLocationResponseModel } from 'src/app/shared/models/response/invetory-location-response-model';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';

@Component({
  selector: 'app-inventory-mapping',
  templateUrl: './inventory-mapping.component.html',
  styleUrls: ['./inventory-mapping.component.sass'],
  standalone: false
})


export class InventoryMappingComponent extends BaseComponent implements OnInit {
  private unsubscriber = new Subject<boolean>();
  private readonly loggedInUserId: number;
  InvetoryLocationOptions: InvetoryLocationResponseModel[] = [];
  inventoryMappingForm: FormGroup;
  facilities: DropdownOption<number>[] = [];
  saveAttempted = false;

  constructor(private fb: FormBuilder,
    messageService: MessageService,
    private settingsService: AdminSettingsService,
    private readonly facilityService: FacilityService,
    private readonly webStorageService: WebStorageService
  ) {
    super(messageService);
    this.initializeForm();
    const loggedInUser = this.webStorageService.getCurrentUserToken();
    this.loggedInUserId = loggedInUser?.userId ?? 0;

  }

  ngOnInit() {
    this.getInventoryLocation();
    this.loadFacilities();
    this.loadInventoryLocationMapping();
  }

  private initializeForm(): void {
    this.inventoryMappingForm = this.fb.group({
      remoteSetup: ['', Validators.required],
      roadHomeVisit: ['', Validators.required],
      facilityVisits: this.fb.array([this.createFacilityVisitRow()])
    });
  }

  get facilityVisitsArray(): FormArray {
    return this.inventoryMappingForm.get('facilityVisits') as FormArray;
  }

  createFacilityVisitRow(): FormGroup {
    return this.fb.group({
      facilities: [[], [this.facilitiesValidator]],
      inventoryLocation: ['', [this.inventoryLocationValidator]]
    }, { validators: [this.facilityVisitRowValidator] });
  }

  addFacilityVisitRow(): void {
    this.facilityVisitsArray.push(this.createFacilityVisitRow());
  }

  removeFacilityVisitRow(index: number): void {
    if (this.facilityVisitsArray.length > 1) {
      this.facilityVisitsArray.removeAt(index);
    }
  }

  getAvailableFacilities(currentIndex: number): DropdownOption<number>[] {
    const selectedFacilities = this.getSelectedFacilities(currentIndex);
    return this.facilities.filter(facility =>
      !selectedFacilities.includes(facility.value)
    );
  }

  getAvailableInventoryLocations(currentIndex: number): InvetoryLocationResponseModel[] {
    const selectedLocations = this.getSelectedInventoryLocationsFromFormArray(currentIndex);
    return this.InvetoryLocationOptions.filter(location =>
      !selectedLocations.includes(location.invetoryLocationId)
    );
  }

  private getSelectedFacilities(excludeIndex: number): number[] {
    const selectedFacilities: number[] = [];

    this.facilityVisitsArray.controls.forEach((control, index) => {
      if (index !== excludeIndex) {
        const facilities = control.get('facilities')?.value || [];
        selectedFacilities.push(...facilities);
      }
    });
    return selectedFacilities;
  }

  private getSelectedInventoryLocationsFromFormArray(excludeIndex: number): number[] {
    const selectedLocations: number[] = [];

    this.facilityVisitsArray.controls.forEach((control, index) => {
      if (index !== excludeIndex) {
        const location = control.get('inventoryLocation')?.value;
        if (location) selectedLocations.push(location);
      }
    });

    return selectedLocations;
  }

  getAllInventoryLocations(): InvetoryLocationResponseModel[] {
    return this.InvetoryLocationOptions;
  }

  private facilitiesValidator(control: AbstractControl): ValidationErrors | null {
    const facilities = control.value;
    if (!facilities || facilities.length === 0) {
      return null;
    }
    return null;
  }

  private inventoryLocationValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    return null;
  }

  private facilityVisitRowValidator(control: AbstractControl): ValidationErrors | null {
    const facilities = control.get('facilities')?.value;
    const inventoryLocation = control.get('inventoryLocation')?.value;

    if (facilities && facilities.length > 0 && !inventoryLocation) {
      return { inventoryLocationRequired: { message: 'Inventory location is required when facilities are selected' } };
    }

    if (inventoryLocation && (!facilities || facilities.length === 0)) {
      return { facilitiesRequired: { message: 'At least one facility is required when inventory location is selected' } };
    }

    return null;
  }

  isFieldInvalid(fieldName: string): boolean {
    if (!this.saveAttempted) return false;
    const field = this.inventoryMappingForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    if (!this.saveAttempted) return '';
    const field = this.inventoryMappingForm.get(fieldName);
    if (field && field.errors && field.touched) {
      const errors = field.errors;
      if (errors['required']) {
        const fieldDisplayName = fieldName === 'remoteSetup' ? 'Remote Setup' : 'Road/Home Visit';
        return `${fieldDisplayName} is required`;
      }
    }
    return '';
  }

  isFacilityVisitRowInvalid(index: number): boolean {
    if (!this.saveAttempted) return false;
    const row = this.facilityVisitsArray.at(index);
    return !!(row && row.invalid);
  }

  getFacilityVisitRowError(index: number): string {
    if (!this.saveAttempted) return '';
    const row = this.facilityVisitsArray.at(index);
    if (row && row.errors) {
      const errors = row.errors;
      if (errors['inventoryLocationRequired']) return errors['inventoryLocationRequired'].message;
      if (errors['facilitiesRequired']) return errors['facilitiesRequired'].message;
    }
    return '';
  }

  isFacilitiesFieldInvalid(index: number): boolean {
    if (!this.saveAttempted) return false;
    const field = this.facilityVisitsArray.at(index)?.get('facilities');
    return !!(field && field.invalid);
  }

  getFacilitiesFieldError(index: number): string {
    if (!this.saveAttempted) return '';
    const field = this.facilityVisitsArray.at(index)?.get('facilities');
    if (field && field.errors) {
      const errors = field.errors;
    }
    return '';
  }

  isInventoryLocationFieldInvalid(index: number): boolean {
    if (!this.saveAttempted) return false;
    const field = this.facilityVisitsArray.at(index)?.get('inventoryLocation');
    return !!(field && field.invalid);
  }


  onSave() {
    this.saveAttempted = true;
    this.markAllFieldsAsTouched();
    this.inventoryMappingForm.updateValueAndValidity();
    
    if (this.inventoryMappingForm.valid) {
      this.loading = true;
      const formData = this.inventoryMappingForm.value;

      const inventoryFacilityMapping: InventoryFacilityMapping[] = [];

      formData.facilityVisits.forEach((visit: any) => {
        if (visit.facilities?.length > 0 && visit.inventoryLocation) {
          visit.facilities.forEach((facilityId: number) => {
            inventoryFacilityMapping.push({
              facilityId: facilityId,
              locationId: visit.inventoryLocation
            });
          });
        }
      });

      const mappingRequest: InventoryLocationMappingRequest = {
        remoteSetup: formData.remoteSetup,
        roadHomeVisit: formData.roadHomeVisit,
        loggedInUserId: this.loggedInUserId,
        inventoryFacilityMapping: inventoryFacilityMapping
      };


      this.facilityService.saveInventoryMapping(mappingRequest)
        .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
        .subscribe({
          next: (result) => {
            if (result.isSuccess) {
              this.showSuccessMessage('Inventory mapping saved successfully!');
              this.saveAttempted = false;
            } else {
              this.showErrorMessage(result.message || 'Failed to save inventory mapping');
            }
          },
          error: (error) => {
            this.showErrorMessage(error?.message || 'Failed to save inventory mapping');
          }
        });
      this.saveAttempted = false;
    } 
  }


  private markAllFieldsAsTouched(): void {
    this.inventoryMappingForm.markAllAsTouched();
    this.facilityVisitsArray.controls.forEach(control => {
      control.markAllAsTouched();
    });
  }

  private getInventoryLocation() {
    this.loading = true;
    this.settingsService.getInvetoryLocation()
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<InvetoryLocationResponseModel[]>) => {
        this.processResult<InvetoryLocationResponseModel[]>(result, () => {
          if (result.isSuccess) {
            if (result.responseModel != undefined && result.responseModel != null) {
              this.InvetoryLocationOptions = result.responseModel;
            }
          }
        });
      }, (httpResponseError) => {
        this.showErrorMessage(httpResponseError.message);
      });
  }

  private loadFacilities() {
    this.loading = true;
    this.facilityService
      .getAllFacilityDropDown()
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe({
        next: ({ responseModel }) => {
          this.facilities = (responseModel ?? []).map(f => ({
            value: f.crmFacilityId,
            label: f.crmFacilityName
          }));
        },
        error: (err) =>
          this.showErrorMessage(err?.message || 'Failed to load facilities')
      });
  }

  private loadInventoryLocationMapping() {
    this.loading = true;
    this.facilityService
      .getInventoryLocationMapping()
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe(
        (result: ResultOfT<InventoryLocationMappingModel[]>) => {
          this.processResult<InventoryLocationMappingModel[]>(result, () => {
            if (result && result.isSuccess) {
              if (result.responseModel != undefined && result.responseModel != null) {
                this.populateFormWithMappingData(result.responseModel);
              }
            }
          });
        },
        (httpResponseError) => {
          this.showErrorMessage(httpResponseError.message);
        }
      );
  }

  private populateFormWithMappingData(data: InventoryLocationMappingModel[]): void {
    const remoteSetup = data.find(item => item.appointmentTypeName === "Remote");
    const roadHomeVisit = data.find(item => item.appointmentTypeName === "RoadHome");

    this.inventoryMappingForm.patchValue({
      remoteSetup: remoteSetup ? remoteSetup.locationId : '',
      roadHomeVisit: roadHomeVisit ? roadHomeVisit.locationId : ''
    });

    while (this.facilityVisitsArray.length !== 0) {
      this.facilityVisitsArray.removeAt(0);
    }

    const locationGroups = new Map<number, number[]>();

    let facilityMapping = data.filter(item => item.facilityId !== null && item.facilityId !== undefined);

    if (facilityMapping && facilityMapping.length > 0) {
      facilityMapping.forEach(mapping => {
        if (!locationGroups.has(mapping.locationId)) {
          locationGroups.set(mapping.locationId, []);
        }
        locationGroups.get(mapping.locationId)!.push(mapping.facilityId);
      });

      locationGroups.forEach((facilityIds, locationId) => {
        const facilityVisitRow = this.fb.group({
          facilities: [facilityIds, [this.facilitiesValidator]],
          inventoryLocation: [locationId, [this.inventoryLocationValidator]]
        }, { validators: [this.facilityVisitRowValidator] });

        this.facilityVisitsArray.push(facilityVisitRow);
      });
    } else {
      this.facilityVisitsArray.push(this.createFacilityVisitRow());
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }
}